<script setup>
import { defineEmits, reactive, computed, unref } from "vue";
import TrashCan from "@/components/shared/icons/TrashCan.vue";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { getLowestProductPrice } from "@/components/program-manager/sessions/utils";
import { formattedPriceFromCents } from "@/util/formatter";
import { TIERS_OBJ } from "@/constants";

const props = defineProps({
  selectedHousing: {
    type: Array,
  },
  selectedUnits: {
    type: Array,
  },
  defaultRoom: {
    type: String,
  },
  session_travel_details: {
    type: Array,
  },
});

const emit = defineEmits(["removeHousing", "checkUnit", "updateDefaultRoomId"]);

const state = reactive({
  expandedHousings: {},
});

const isHousingExpanded = (housingId) => {
  return !!state.expandedHousings[housingId];
};
const setHouseExpanded = (housingId) => {
  state.expandedHousings[housingId] = !isHousingExpanded(housingId);
};

const isSelectedUnit = (unitId) => {
  return props.selectedUnits?.findIndex(({ id }) => id === unitId) >= 0;
};

const removeHousing = (housingId) => {
  emit("removeHousing", housingId);
};

const checkUnit = (unitId) => {
  emit("checkUnit", unitId);
};

const tripDuration = computed(() => {
  const duration =
    props?.session_travel_details?.length > 0 &&
    props.session_travel_details[0]?.duration;

  return duration >= 0 ? duration : 0;
});

const roomStartDate = computed(() => {
  return (
    props?.session_travel_details?.length > 0 &&
    props.session_travel_details[0]?.arrival_date
  );
});

const getLowestRoomCostDaily = (room) =>
  getLowestProductPrice(room, unref(roomStartDate));

const roomCostDaily = (room) => {
  const lowestProductPrice = getLowestRoomCostDaily(room);
  return formattedPriceFromCents(lowestProductPrice?.price_in_cents);
};

const roomCostTotal = (room) => {
  let total = 0;
  if (unref(tripDuration)) {
    const lowestProductPrice = getLowestRoomCostDaily(room);
    total = formattedPriceFromCents(
      lowestProductPrice?.price_in_cents * unref(tripDuration)
    );
  }
  return total;
};

const housingName = (housing) => {
  const tierName = TIERS_OBJ?.[housing?.tier?.name] ?? "";
  if (tierName) {
    return `${housing?.name ?? ""} - ${tierName} ${housing?.type ?? ""}`;
  }
  return housing?.name ?? "";
};
</script>

<template>
  <div class="flex flex-col col-span-2">
    <div class="block text-xs font-semibold text-gray-700">From Library</div>
    <div
      class="grid grid-cols-11 gap-x4 gap-y-3 bg-white border-gray-300 rounded-md w-full p-4 mt-2"
    >
      <div class="block text-xs font-semibold text-gray-700 col-span-4">
        Name
      </div>
      <div
        class="block text-xs font-semibold text-gray-700 col-span-1 text-center"
      >
        Default
      </div>
      <div class="block text-xs font-semibold text-gray-700 col-span-3">
        Vendor
      </div>
      <div
        class="block text-xs font-semibold text-gray-700 col-span-1 text-right"
      >
        Total Price /bed/day
      </div>
      <div
        class="block text-xs font-semibold text-gray-700 col-span-1 text-right"
      >
        Total Price /bed
      </div>
      <div class="col-span-1"></div>
      <template v-for="housing in selectedHousing">
        <div class="col-start-1 col-end-9 flex items-center">
          <ChevronRightIcon
            class="w-4 h-4 mr-1 cursor-pointer"
            :class="{ rotated: isHousingExpanded(housing.id) }"
            @click="setHouseExpanded(housing.id)"
          />

          <div class="text-teal-900 text-sm">
            <a
              class="text-teal-900"
              :href="`/program-manager/housing/${housing.id}`"
              target="_blank"
              >{{ housingName(housing) }}</a
            >
          </div>
        </div>
        <div class="col-start-9 col-end-11"></div>
        <div class="col-span-1 mx-auto">
          <TrashCan
            class="w-[18px] ml-2 cursor-pointer"
            @click="removeHousing(housing)"
          />
        </div>
        <template v-if="isHousingExpanded(housing.id)">
          <template v-for="unit in housing.units">
            <div class="col-start-1 col-end-12 flex text-sm items-center">
              <input
                type="checkbox"
                class="h-4 w-4 border-gray-300 form-checkbox ml-12 mr-2"
                :checked="isSelectedUnit(unit.id)"
                @input="checkUnit(unit)"
              />
              {{ unit?.name || "" }}
            </div>
            <template v-for="(room, roomIndex) in unit.rooms">
              <div
                class="col-start-1 col-end-5 flex items-center text-sm ml-24"
              >
                {{ `Room ${roomIndex + 1} - ${room?.type ?? ""}` }}
              </div>
              <div class="col-start-5 col-end-6 flex items-center">
                <input
                  type="checkbox"
                  class="h-4 w-4 border-gray-400 form-radio mx-auto"
                  :disabled="!isSelectedUnit(unit.id)"
                  :checked="props.defaultRoom === room.id"
                  @input="$emit('updateDefaultRoomId', room.id)"
                />
              </div>
              <div class="col-start-6 col-end-9 flex items-center text-sm">
                {{ housing?.vendor?.name ?? "" }}
              </div>
              <div class="col-start-9 col-end-10 text-sm text-right">
                {{ roomCostDaily(room) }}
              </div>
              <div class="col-start-10 col-end-11 text-sm text-right">
                {{ roomCostTotal(room) }}
              </div>
            </template>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.rotated {
  transform: rotate(90deg);
}
</style>
